import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  BarChart3, BookOpen, Boxes, BriefcaseBusiness, ChevronDown, CircleGauge, CircleHelp, CloudUpload,
  FileJson2, FlaskConical, FolderOpen, Globe2, Handshake, History, Import, Lightbulb, LogOut,
  Menu, PanelLeftClose, PanelLeftOpen, RefreshCw, Settings, Shield, Sparkles, SquareTerminal, Swords, UserRound, Users
} from 'lucide-react';
import { loadDirectoryHandle, savedViewRepository } from '../data/repositories';
import { importDirectory, importDroppedItems, importFileList, loadDemoData } from '../data/importer';
import { matchesFilter } from '../domain/query';
import { authConfigured } from '../lib/api';
import { useAuthStore } from '../state/authStore';
import { filterCount, parseFilterFromSearch, serializeFilterToSearch } from '../state/filterUrl';
import { useAppStore } from '../state/appStore';
import { ActiveFilters, CohortDialog, FilterActions, FilterDialog, FilterTrigger } from './Filters';
import { ErrorBoundary } from './ErrorBoundary';
import { PwaUpdatePrompt } from './PwaUpdatePrompt';
import { Button } from './UI';
import styles from './AppShell.module.css';

const baseNav = [
  { section: '总览', to: '/dashboard', label: '指挥中心', icon: CircleGauge },
  { section: '总览', to: '/insights', label: '个人洞察', icon: Lightbulb },
  { section: '分析', to: '/workbench', label: '分析工作台', icon: FlaskConical },
  { section: '分析', to: '/runs', label: '游戏记录', icon: History },
  { section: '图鉴', to: '/cards', label: '卡牌', icon: BookOpen },
  { section: '图鉴', to: '/relics', label: '遗物', icon: Boxes },
  { section: '图鉴', to: '/encounters', label: '遭遇战', icon: Swords },
  { section: '图鉴', to: '/ancients', label: '先古之民', icon: Sparkles },
  { section: '生涯', to: '/coop', label: '多人合作', icon: Handshake },
  { section: '生涯', to: '/career', label: '生涯统计', icon: BriefcaseBusiness },
  { section: '系统', to: '/settings', label: '设置', icon: Settings },
  { section: '系统', to: '/how-to', label: '导入指南', icon: CircleHelp }
];

const mobilePrimaryRoutes = new Set(['/dashboard', '/insights', '/workbench', '/cards', '/runs']);
const mobileAdminPrimaryRoutes = new Set(['/', '/dashboard', '/workbench', '/runs', '/settings']);

export function AppShell({ portal = 'user' }: { portal?: 'user' | 'admin' }) {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const folderInput = useRef<HTMLInputElement>(null);
  const runInput = useRef<HTMLInputElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const dragDepth = useRef(0);
  const syncingFromLocation = useRef(false);
  const [toast, setToast] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [hasSavedDirectory, setHasSavedDirectory] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('sts2stats.sidebarCollapsed') === 'true'; }
    catch { return false; }
  });
  const { profile, mode, signOut } = useAuthStore();
  const {
    runs, localRuns, localProgress, dataScope, viewedAccount, overallSummary, overallMeta, latestDataset, filter, filterHistory, cohorts,
    loading, hydrated, workerBusy, cloudLoading, lastRefresh, error, hydrate, reloadRuns,
    switchDataScope, uploadLatestDataset, setFilter, clearFilters, undoFilter, setCohort,
    setWorkerBusy, setError, refreshSavedViews
  } = useAppStore();
  const nav = (portal === 'admin'
    ? [{ section: '管理', to: '/', label: '管理员面板', icon: Shield }, ...baseNav]
    : baseNav).filter((item) => dataScope !== 'overall' || item.to !== '/runs');
  const filteredCount = useMemo(() => dataScope === 'overall' ? overallMeta?.runCount || 0 : runs.filter((run) => run.sourceScope !== 'imported' && matchesFilter(run, filter)).length, [dataScope, overallMeta?.runCount, runs, filter]);
  const activeCount = filterCount(filter);
  const scopeTitle = dataScope === 'overall' ? '总体数据' : dataScope === 'admin' ? `${viewedAccount?.displayName || '账号'}的数据` : '个人数据';
  const totalRuns = runs.filter((run) => run.sourceScope !== 'imported').length;
  const currentPage = [...nav].sort((left, right) => right.to.length - left.to.length).find((item) => item.to === '/' ? location.pathname === '/' : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));

  useEffect(() => { void hydrate(); }, [hydrate]);
  useEffect(() => { void loadDirectoryHandle().then((handle) => setHasSavedDirectory(Boolean(handle))).catch(() => setHasSavedDirectory(false)); }, []);
  useEffect(() => { mainRef.current?.focus(); }, [dataScope, location.pathname, viewedAccount?.id]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasFilterParams = ['mode', 'characters', 'builds', 'ascensions', 'outcomes', 'includeCards', 'excludeCards', 'includeRelics', 'excludeRelics', 'party', 'from', 'to', 'minTime', 'maxTime'].some((key) => params.has(key));
    if (navigationType !== 'POP' && !hasFilterParams) return;
    const locationFilter = parseFilterFromSearch(location.search);
    if (JSON.stringify(locationFilter) === JSON.stringify(filter)) return;
    syncingFromLocation.current = true;
    setFilter(locationFilter, false);
  }, [filter, location.pathname, location.search, navigationType, setFilter]);
  useEffect(() => {
    if (syncingFromLocation.current) { syncingFromLocation.current = false; return; }
    const nextSearch = serializeFilterToSearch(filter, location.search);
    if (nextSearch === location.search) return;
    navigate({ pathname: location.pathname, search: nextSearch, hash: location.hash }, { replace: true });
  }, [filter, location.hash, location.pathname, location.search, navigate]);
  useEffect(() => { if (!toast) return; const duration = Math.min(7200, Math.max(4200, 2500 + toast.length * 45)); const timer = window.setTimeout(() => setToast(''), duration); return () => clearTimeout(timer); }, [toast]);
  useEffect(() => { if (error) { setToast(error); setError(null); } }, [error, setError]);
  useEffect(() => { try { localStorage.setItem('sts2stats.sidebarCollapsed', String(sidebarCollapsed)); } catch { /* local preference is best-effort */ } }, [sidebarCollapsed]);

  const runImport = async (task: () => Promise<Awaited<ReturnType<typeof loadDemoData>>>, label: string, syncCloud = true) => {
    setWorkerBusy(true);
    try {
      await switchDataScope('personal');
      const result = await task();
      const unchanged = result.changes.added === 0 && result.changes.updated === 0 && result.changes.missing.length === 0 && result.changes.unchanged > 0;
      if (unchanged) {
        setToast(`${label}完成：未发现存档文件变化，已跳过重复解析和云端上传。`);
        return result;
      }
      await reloadRuns();
      const incremental = result.changes.updated > 0 || result.changes.missing.length > 0 || result.changes.unchanged > 0;
      const changeSummary = `${result.changes.added} 新增 · ${result.changes.updated} 更新 · ${result.changes.missing.length} 移除 · ${result.changes.unchanged} 复用`;
      if (syncCloud && authConfigured && profile) {
        try {
          const dataset = await uploadLatestDataset(result.delta);
          setToast(`${label}完成：${incremental ? `本地增量更新（${changeSummary}）` : `已建立 ${result.runs.length} 局完整基线`}，云端${dataset.syncMode === 'incremental' ? '增量同步' : '完整同步'}后共 ${dataset.runCount} 局。`);
        } catch {
          setError(null);
          setToast(`${label}已在本地${incremental ? '完成增量更新' : '建立完整基线'}，但云端同步失败。请从账号菜单重试同步。`);
        }
      } else {
        setToast(`${label}完成：${incremental ? `本地增量更新（${changeSummary}）` : `已建立 ${result.runs.length} 局完整基线`}${syncCloud ? '' : '；演示数据不会同步云端'}。`);
      }
      return result;
    } catch (importError) {
      setToast(importError instanceof Error ? importError.message : String(importError));
      return null;
    } finally { setWorkerBusy(false); }
  };

  const pickDirectory = async () => {
    try {
      if ('showDirectoryPicker' in window) {
        const savedHandle = await loadDirectoryHandle();
        const handle = await (window as Window & { showDirectoryPicker: (options?: { id?: string; mode?: 'read'; startIn?: FileSystemDirectoryHandle | 'desktop' | 'documents' | 'downloads' | 'music' | 'pictures' | 'videos' }) => Promise<FileSystemDirectoryHandle> }).showDirectoryPicker({ id: 'sts2-save-directory', mode: 'read', startIn: savedHandle || 'documents' });
        const result = await runImport(() => importDirectory(handle, profile?.id || null), savedHandle ? '存档目录更换' : '存档导入');
        if (result) setHasSavedDirectory(true);
      } else {
        folderInput.current?.click();
      }
    } catch (pickerError) {
      if (pickerError instanceof DOMException && pickerError.name === 'AbortError') return;
      setToast(pickerError instanceof Error ? pickerError.message : String(pickerError));
    }
  };

  const refreshSavedDirectory = async () => {
    try {
      const handle = await loadDirectoryHandle();
      if (!handle) {
        setHasSavedDirectory(false);
        setToast('尚未保存存档目录，请先选择 saves 文件夹。');
        return;
      }
      const permission = await handle.queryPermission({ mode: 'read' });
      const granted = permission === 'granted' || await handle.requestPermission({ mode: 'read' }) === 'granted';
      if (!granted) {
        setToast('已保存目录的读取权限已失效，请使用“选择 / 更换文件夹”重新授权。');
        return;
      }
      await runImport(() => importDirectory(handle, profile?.id || null), '存档刷新');
    } catch (refreshError) {
      setToast(refreshError instanceof Error ? refreshError.message : String(refreshError));
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setToast('分析链接已复制；筛选条件已包含在 URL 中。');
  };

  const saveCurrentView = async () => {
    const now = Date.now();
    await savedViewRepository.save({
      id: crypto.randomUUID(),
      name: `指挥中心视图 ${new Date(now).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}`,
      query: { id: crypto.randomUUID(), dataSource: 'runs', metricIds: ['win_rate', 'avg_floor'], dimensionIds: ['character'], filter, sort: [{ field: 'sample', direction: 'desc' }], limit: 50, visualization: 'bar' },
      layout: { span: 12, order: 0 }, createdAt: now, updatedAt: now
    });
    await refreshSavedViews();
    setToast('当前筛选已保存为本地分析视图。');
  };

  const chooseScope = async (scope: 'personal' | 'overall') => {
    if (scope === 'overall' && !authConfigured) {
      setToast('总体数据需要先启动自建账号服务。');
      return;
    }
    try { await switchDataScope(scope); }
    catch { /* store error is surfaced through the shared toast */ }
  };

  const upload = async () => {
    if (!localRuns.some((run) => run.sourceScope === 'history') && !localProgress) {
      setToast('请先导入个人存档，再同步云端。');
      return;
    }
    try {
      const dataset = await uploadLatestDataset();
      setToast(`当前存档已重新同步：${dataset.runCount} 局。总体统计只使用这一份数据。`);
    } catch { /* store error is surfaced through the shared toast */ }
  };

  const hasDraggedFiles = (event: React.DragEvent) => [...event.dataTransfer.types].includes('Files');
  const onDragEnter = (event: React.DragEvent) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    dragDepth.current += 1;
    setDragActive(true);
  };
  const onDragOver = (event: React.DragEvent) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };
  const onDragLeave = (event: React.DragEvent) => {
    if (!hasDraggedFiles(event)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (!dragDepth.current) setDragActive(false);
  };
  const onDrop = (event: React.DragEvent) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    dragDepth.current = 0;
    setDragActive(false);
    if (workerBusy || cloudLoading || dataScope !== 'personal') return;
    const task = importDroppedItems(event.dataTransfer, profile?.id || null);
    void runImport(() => task, '拖放导入');
  };

  return <div className={`${styles.shell} ${sidebarCollapsed ? styles.shellCollapsed : ''}`} onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
    <a className={styles.skipLink} href="#main-content">跳到主内容</a>
    {dragActive && <div className={styles.dropOverlay}><div><FolderOpen size={34} /><strong>放下 saves 文件夹</strong><span>完整解析成功后替换当前存档</span></div></div>}
    <aside className={styles.sidebar}>
      <NavLink to={portal === 'admin' ? '/' : '/dashboard'} className={styles.brand} aria-label={portal === 'admin' ? 'STS2 管理终端首页' : 'STS2 数据终端首页'}><span className={styles.brandMark}><SquareTerminal size={19} aria-hidden="true" /></span><span className={styles.brandCopy}><strong>{portal === 'admin' ? 'STS2 管理终端' : 'STS2 数据终端'}</strong><span>{portal === 'admin' ? 'ADMIN_TTY // SECURE' : 'RUN_INTEL // ONLINE'}</span></span></NavLink>
      <nav className={styles.nav} aria-label="主导航">{nav.map((item, index) => { const section = index === 0 || nav[index - 1]?.section !== item.section ? <div className={styles.navSection}>{item.section}</div> : null; const Icon = item.icon; const mobilePrimary = (portal === 'admin' ? mobileAdminPrimaryRoutes : mobilePrimaryRoutes).has(item.to); return <Fragment key={item.to}>{section}<NavLink to={item.to} data-mobile-primary={mobilePrimary ? 'true' : 'false'} className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ''}`} title={item.label}><Icon size={17} /><span>{item.label}</span></NavLink></Fragment>; })}</nav>
      <div className={styles.privacy}><span className={`${styles.sourceDot} ${runs.length ? '' : styles.sourceDotEmpty}`} /><div><strong>{portal === 'admin' ? '管理域已隔离' : authConfigured ? '存档已受保护' : '本地数据模式'}</strong><span>{portal === 'admin' ? '权限由服务端校验' : authConfigured ? '本地解析 · 单份同步' : '数据仅保存在浏览器'}</span></div></div>
    </aside>

    <header className={styles.topbar}>
      <button type="button" className={styles.sidebarToggle} aria-label={sidebarCollapsed ? '展开侧栏' : '收起侧栏'} onClick={() => setSidebarCollapsed((value) => !value)}>{sidebarCollapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}</button>
      <DropdownMenu.Root><DropdownMenu.Trigger asChild><button type="button" className={styles.mobileMenu} aria-label="打开全部导航"><Menu size={19} /></button></DropdownMenu.Trigger><DropdownMenu.Portal><DropdownMenu.Content className={`${styles.importMenu} ${styles.mobileNavMenu}`} align="start" sideOffset={8}>
        <div className={styles.mobileNavHeader}>全部页面</div>
        {nav.map((item) => { const Icon = item.icon; return <DropdownMenu.Item asChild key={item.to}><NavLink to={item.to} className={styles.mobileNavItem}><Icon size={16} /><span>{item.label}</span></NavLink></DropdownMenu.Item>; })}
      </DropdownMenu.Content></DropdownMenu.Portal></DropdownMenu.Root>
      <div className={styles.terminalControls} aria-hidden="true"><i /><i /><i /></div>
      <div className={styles.topbarLocation}><span>{portal === 'admin' ? '管理终端' : 'STS2 数据终端'}</span><strong>{currentPage?.label || '数据分析'}</strong></div>
      <div className={styles.topbarSpacer} />
      <div className={styles.scopeToggle} aria-label="数据范围">
        <button type="button" aria-pressed={dataScope === 'personal'} className={dataScope === 'personal' ? styles.scopeActive : ''} onClick={() => void chooseScope('personal')}><UserRound size={13} />个人</button>
        <button type="button" aria-pressed={dataScope === 'overall'} className={dataScope === 'overall' ? styles.scopeActive : ''} onClick={() => void chooseScope('overall')}><Globe2 size={13} />总体</button>
      </div>
      <div className={styles.source}><div className={styles.sourceTop}><span className={`${styles.sourceDot} ${runs.length ? '' : styles.sourceDotEmpty}`} />{scopeTitle}</div><div className={styles.sourceMeta}>{cloudLoading ? '正在同步云端数据…' : dataScope === 'overall' ? `${overallSummary?.accountCount || 0} 个账号的唯一数据集` : dataScope === 'admin' ? '管理员个人视图' : latestDataset ? `云端存档 · ${latestDataset.runCount} 局${latestDataset.revision ? ` · r${latestDataset.revision}` : ''}` : lastRefresh ? `刷新于 ${new Date(lastRefresh).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}` : '本地单一存档'}</div></div>
      <div className={styles.actions}>
        {hasSavedDirectory && <Button primary className={styles.importButton} disabled={workerBusy || cloudLoading || dataScope !== 'personal'} onClick={() => void refreshSavedDirectory()} title={dataScope !== 'personal' ? '切换到个人数据后才能刷新' : '直接从已授权目录读取最新存档'}>{workerBusy ? <RefreshCw size={14} className="spin" /> : <RefreshCw size={14} />}{workerBusy ? '处理中' : '刷新存档'}</Button>}
        <DropdownMenu.Root><DropdownMenu.Trigger asChild><Button primary={!hasSavedDirectory} icon={hasSavedDirectory} className={hasSavedDirectory ? styles.importMoreButton : styles.importButton} disabled={workerBusy || cloudLoading || dataScope !== 'personal'} aria-label={hasSavedDirectory ? '导入或更换存档目录' : undefined} title={dataScope !== 'personal' ? '切换到个人数据后才能导入' : undefined}>{hasSavedDirectory ? <ChevronDown size={14} /> : <><Import size={14} />选择存档<ChevronDown size={13} /></>}</Button></DropdownMenu.Trigger><DropdownMenu.Portal><DropdownMenu.Content className={styles.importMenu} align="end" sideOffset={7}>
          <DropdownMenu.Item className={styles.menuItem} onSelect={pickDirectory}><FolderOpen size={15} />{hasSavedDirectory ? '选择 / 更换存档文件夹' : '选择存档文件夹'}</DropdownMenu.Item>
          <DropdownMenu.Item className={styles.menuItem} onSelect={() => folderInput.current?.click()}><FileJson2 size={15} />从文件夹输入替换当前存档</DropdownMenu.Item>
          <DropdownMenu.Item className={styles.menuItem} onSelect={() => runInput.current?.click()}><Import size={15} />用选中文件替换当前存档</DropdownMenu.Item>
          <DropdownMenu.Item data-demo-import className={styles.menuItem} onSelect={() => runImport(() => loadDemoData(profile?.id || null), '演示数据载入', false)}><Sparkles size={15} />载入演示数据</DropdownMenu.Item>
        </DropdownMenu.Content></DropdownMenu.Portal></DropdownMenu.Root>
        <DropdownMenu.Root><DropdownMenu.Trigger asChild><button type="button" className={styles.accountButton} aria-label="账号菜单"><span>{profile?.displayName?.slice(0, 1).toUpperCase() || (mode === 'local' ? 'L' : 'U')}</span><ChevronDown size={12} /></button></DropdownMenu.Trigger><DropdownMenu.Portal><DropdownMenu.Content className={styles.accountMenu} align="end" sideOffset={7}>
          <div className={styles.accountHeader}><strong>{profile?.displayName || (mode === 'local' ? '本地预览' : '玩家')}</strong><span>{profile?.email || (mode === 'local' ? '未连接账号服务' : '')}</span></div>
          {authConfigured && <DropdownMenu.Item className={styles.menuItem} disabled={cloudLoading || workerBusy} onSelect={() => void upload()}><CloudUpload size={15} />重建云端同步基线</DropdownMenu.Item>}
          {portal === 'admin' && <DropdownMenu.Item className={styles.menuItem} onSelect={() => navigate('/')}><Users size={15} />账号管理</DropdownMenu.Item>}
          {authConfigured && <DropdownMenu.Item className={styles.menuItem} onSelect={() => void signOut().catch((signOutError) => setToast(signOutError instanceof Error ? signOutError.message : '退出失败，请稍后重试。'))}><LogOut size={15} />退出登录</DropdownMenu.Item>}
        </DropdownMenu.Content></DropdownMenu.Portal></DropdownMenu.Root>
      </div>
      <input ref={folderInput} hidden type="file" multiple {...({ webkitdirectory: '', directory: '' } as React.InputHTMLAttributes<HTMLInputElement>)} onChange={(event) => { const files = event.currentTarget.files ? [...event.currentTarget.files] : []; event.currentTarget.value = ''; if (files.length) void runImport(() => importFileList(files, 'folder-input', profile?.id || null), '文件夹导入'); }} />
      <input ref={runInput} hidden type="file" multiple accept=".run,.save,application/json" onChange={(event) => { const files = event.currentTarget.files ? [...event.currentTarget.files] : []; event.currentTarget.value = ''; if (files.length) void runImport(() => importFileList(files, 'single', profile?.id || null), '文件导入'); }} />
    </header>

    <div className={styles.filterbar} aria-label="全局分析上下文">
      <FilterDialog runs={runs} filter={filter} onApply={setFilter} trigger={<FilterTrigger count={activeCount} />} options={dataScope === 'overall' ? overallMeta?.filterOptions : undefined} />
      <div className={styles.topFilters}>{activeCount ? <ActiveFilters filter={filter} onChange={setFilter} /> : <span className={styles.allDataLabel}>全部游戏</span>}</div>
      <div className={styles.sample}><strong>{filteredCount.toLocaleString('zh-CN')}</strong><span>/ {(dataScope === 'overall' ? overallSummary?.runCount : totalRuns)?.toLocaleString('zh-CN') || 0} 局</span></div>
      <div className={styles.analysisActions}><CohortDialog runs={runs} cohorts={cohorts} onChange={(next) => next.forEach(setCohort)} trigger={<Button><BarChart3 size={14} />A/B</Button>} options={dataScope === 'overall' ? overallMeta?.filterOptions : undefined} /><FilterActions onUndo={undoFilter} onClear={clearFilters} onSave={saveCurrentView} onCopy={copyLink} canUndo={filterHistory.length > 0} active={activeCount > 0} /></div>
    </div>

    {dataScope === 'admin' && <div className={styles.adminNotice}><span><Shield size={14} />正在以 <strong>{viewedAccount?.displayName}</strong>（{viewedAccount?.email}）的个人视图查看数据。</span><Button onClick={() => void chooseScope('personal')}>退出代入查看</Button></div>}
    <main id="main-content" ref={mainRef} tabIndex={-1} className={styles.main} key={`${location.pathname}:${dataScope}:${viewedAccount?.id || ''}`}>{loading && !hydrated ? <div className={styles.startupLoading} role="status"><RefreshCw size={20} className="spin" /><div><strong>正在载入本地存档</strong><span>本地缓存就绪后会立即显示，云端更新将在后台检查。</span></div></div> : <ErrorBoundary resetKey={`${location.pathname}:${dataScope}:${viewedAccount?.id || ''}`}><Outlet context={{ navigate, setToast, loadDemo: () => void runImport(() => loadDemoData(profile?.id || null), '演示数据载入', false) }} /></ErrorBoundary>}</main>
    {toast && <div className={styles.toast} role="status">{toast}</div>}
    {portal === 'user' && <PwaUpdatePrompt />}
  </div>;
}
